import { Globals, animated, useReducedMotion, useTransition } from '@react-spring/web';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import useScrollToTop from '../../hooks/useScrollToTop';

import { useCatalog, useMenuOpen, useMenus } from '../../contexts/SiteContext';
import { useToasts } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import Footer from '../partials/Footer';
import Header from '../partials/Header';
import Toasts from '../partials/Toasts';
import Title from '../typography/Title';

import styles from '../../../styles/layouts/main.module.css';

const propTypes = {
    page: PropTypes.shape({}),
    theme: PropTypes.string,
    loading: PropTypes.bool,
    children: PropTypes.node.isRequired,
};

function MainLayout({ page = null, loading = false, theme = 'dark', children = null }) {
    const { main = null, pro = null, footer = null, handles = null } = useMenus();
    const catalog = useCatalog();
    const [pathname = null] = useLocation();
    const { menuOpen } = useMenuOpen();
    const toasts = useToasts();
    const user = useUser();

    const { theme: pageTheme = null, footerFilm = null } = page || {};
    const isPro = pageTheme === 'pro';
    const isConnected = user !== null;
    const reducedMotion = useReducedMotion();

    useScrollToTop(page, pathname);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    useEffect(() => {
        Globals.assign({
            skipAnimation: reducedMotion || false,
        });
        return () => {
            Globals.assign({
                skipAnimation: false,
            });
        };
    }, [reducedMotion]);

    const transitions = useTransition([children], {
        keys: ({ type: { name } }) => name,
        initial: { opacity: 1, y: 0, position: 'relative' },
        from: { opacity: 0, y: 0, position: 'fixed' },
        update: {
            opacity: loading ? 0 : 1,
            y: 0,
            position: 'relative',
        },
        enter: { opacity: 1, y: 0, position: 'fixed' },
        leave: { opacity: 0, y: 0, position: 'fixed' },
        config: {
            mass: 1,
            tension: 200,
            friction: 20,
            clamp: true,
        },
        trail: 100,
    });

    const button = useMemo(
        () => (handles || []).find(({ handle = null }) => handle === 'pro'),
        [handles],
    );

    return (
        <div className={styles.container} data-theme={pageTheme || theme}>
            <Header
                className={styles.header}
                menu={isPro ? pro : main}
                footerMenu={footer}
                button={button}
                catalog={catalog}
                isPro={isPro}
                isConnected={isConnected}
            />
            <Toasts className={styles.toasts} toasts={toasts} />
            {isPro && !isConnected ? (
                <Title className={styles.largeTitle} size="large" gradient>
                    <FormattedMessage defaultMessage="Pro corner" description="Page title" />
                </Title>
            ) : null}
            <div className={styles.contained}>
                {transitions((style, animatedChildren) => (
                    <animated.main className={styles.content} style={{ ...style, width: '100%' }}>
                        {animatedChildren}
                    </animated.main>
                ))}
            </div>
            {!isPro ? (
                <Footer className={styles.footer} menu={footer} footerFilm={footerFilm} />
            ) : null}
        </div>
    );
}

MainLayout.propTypes = propTypes;

export default MainLayout;
